import { MATERIALS } from '../world/city';
import { physicalCenter } from '../destruction/structure';
import { PhysicsCity, type BlastEvent } from '../physics/world';
import { FireSystem } from './fire';
import { Effects } from '../render/effects';

/** One finite thermal zone per authored object, with local member heat damage. */
export class Environment {
  private anchors = new Map<number, number>();
  private points = new Map<number, { x: number; y: number; z: number }>();
  private fixedPoints = new Map<number, { cell: number; owner: number; revision: number }>();
  private pending = new Map<number, { heat: number; elapsed: number; queued: boolean; run: () => void }>();
  private seen = new Map<number, Set<number>>();
  private marked = new Set<number>();
  private assessedHeat = new Map<number, { heat: number; time: number }>();
  private markChanged = (id: number): void => this.fire.invalidatePosition(`structure:${id}`);
  fuelScans = 0;
  fuelCellsVisited = 0;
  constructor(
    readonly physics: PhysicsCity,
    readonly fire: FireSystem,
    readonly effects: Effects,
  ) {}
  reset(): void {
    for (const step of this.resetSteps()) void step;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    this.physics.supportObservers.add(this.markChanged);
    let count = 0;
    for (const b of this.physics.buildings) {
      const combustibles = b.cells.filter((c) => MATERIALS[c.material].fuel > 0);
      const preferred =
        (combustibles.length ? combustibles : b.cells).find((c) => c.id % 6 !== 0) ?? b.cells[0];
      if (!preferred) continue;
      this.anchors.set(b.id, preferred.id);
      this.points.set(b.id, { x: b.x + b.w * 0.625, y: (b.baseY ?? 0.16) + 0.2, z: b.z + b.d * 0.625 });
      const originalFuel = Math.max(
        1,
        combustibles.reduce((n, c) => n + MATERIALS[c.material].fuel, 0),
      );
      let fuelRevision = -1,
        fuel = 0;
      this.fire.register({
        id: `structure:${b.id}`,
        category: b.kind === 'tree' ? 'vegetation' : combustibles.length ? 'wood' : 'building',
        // A small combustible furnishing is still a complete fuel source. The
        // former denominator of 18 made a chair burn out before crews could
        // even board. Object size sets a finite game-duration instead.
        burnSeconds: Math.min(240, 45 + Math.sqrt(combustibles.length) * 12),
        position: () => this.position(b.id),
        positionFixed: () => {
          const anchor = this.anchors.get(b.id),
            owner = anchor === undefined ? undefined : this.physics.cellOwner.get(anchor);
          return owner === undefined || this.physics.staticSupportVersion(owner) !== undefined;
        },
        fuel: () => {
          const revision = this.physics.buildingSupportVersion(b.id);
          if (revision !== fuelRevision) {
            fuelRevision = revision;
            this.fuelScans++;
            this.fuelCellsVisited += combustibles.length;
            fuel = Math.min(
              1,
              combustibles.reduce(
                (n, c) => n + (this.physics.cellOwner.has(c.id) ? MATERIALS[c.material].fuel : 0),
                0,
              ) / originalFuel,
            );
          }
          return fuel;
        },
        onHeat: (heat, dt) => this.scheduleHeat(b.id, heat, dt),
      });
      if (++count % 64 === 0) yield;
    }
  }
  blast(e: BlastEvent): void {
    if (!this.physics.life.valid(e.epoch) || this.physics.life.phase !== 'playing') return;
    let seen = this.seen.get(e.id);
    if (!seen) {
      if (this.seen.size >= 96) this.seen.delete(this.seen.keys().next().value!);
      this.seen.set(e.id, (seen = new Set()));
    }
    for (const b of this.physics.buildings) {
      if (seen.has(b.id)) continue;
      const x = Math.max(b.x, Math.min(b.x + b.w * 1.25, e.point.x)),
        y = Math.max(b.baseY ?? 0.16, Math.min((b.baseY ?? 0.16) + b.h * 1.25, e.point.y)),
        z = Math.max(b.z, Math.min(b.z + b.d * 1.25, e.point.z));
      if (Math.hypot(x - e.point.x, y - e.point.y, z - e.point.z) > e.radius) continue;
      seen.add(b.id);
      let nearest: number | undefined,
        distance = Infinity;
      for (const cell of this.physics.buildingCells(b.id)) {
        const point = this.physics.cellPosition(cell.id);
        if (!point) continue;
        const d = (point.x - e.point.x) ** 2 + (point.y - e.point.y) ** 2 + (point.z - e.point.z) ** 2;
        if (d < distance) {
          nearest = cell.id;
          distance = d;
        }
      }
      if (nearest !== undefined) {
        this.anchors.set(b.id, nearest);
        this.position(b.id);
        this.fire.invalidatePosition(`structure:${b.id}`);
      }
    }
    if (e.heat > 0 && e.radius > 0 && !this.marked.has(e.id) && e.point.y < e.radius + 1) {
      this.marked.add(e.id);
      if (this.marked.size > 96) this.marked.delete(this.marked.values().next().value!);
      this.effects.mark(e.point, Math.min(9, e.radius * 0.6));
    }
  }
  private position(id: number): { x: number; y: number; z: number } {
    const anchor = this.anchors.get(id),
      owner = anchor === undefined ? undefined : this.physics.cellOwner.get(anchor),
      revision = owner === undefined ? undefined : this.physics.staticSupportVersion(owner),
      cached = this.fixedPoints.get(id);
    if (cached && cached.cell === anchor && cached.owner === owner && cached.revision === revision)
      return this.points.get(id)!;
    this.fixedPoints.delete(id);
    const point = anchor === undefined ? null : this.physics.cellPosition(anchor);
    if (point) {
      const saved = { x: point.x, y: point.y, z: point.z };
      this.points.set(id, saved);
      if (revision !== undefined && owner !== undefined && anchor !== undefined)
        this.fixedPoints.set(id, { cell: anchor, owner, revision });
      return saved;
    }
    const last = this.points.get(id) ?? { x: 0, y: 0, z: 0 };
    let nearest: number | undefined,
      best = Infinity,
      replacement = last;
    for (const cell of this.physics.buildingCells(id)) {
      const p = this.physics.cellPosition(cell.id);
      if (!p) continue;
      const d = (p.x - last.x) ** 2 + (p.y - last.y) ** 2 + (p.z - last.z) ** 2;
      if (d < best) {
        best = d;
        nearest = cell.id;
        replacement = { x: p.x, y: p.y, z: p.z };
      }
    }
    if (nearest !== undefined) {
      this.anchors.set(id, nearest);
      this.points.set(id, replacement);
    } else this.anchors.delete(id);
    return replacement;
  }
  private scheduleHeat(id: number, heat: number, dt: number): void {
    if (this.physics.life.phase !== 'playing') return;
    const pending = this.pending.get(id);
    if (pending) {
      pending.heat = heat;
      pending.elapsed += dt;
      return;
    }
    const entry = { heat, elapsed: dt, queued: false, run: () => {} };
    this.pending.set(id, entry);
    const run = () => {
      if (this.pending.get(id) !== entry) return;
      this.pending.delete(id);
      this.position(id);
      const anchor = this.anchors.get(id);
      if (anchor === undefined) return;
      const owner = this.physics.cellOwner.get(anchor);
      if (owner === undefined) return;
      const c = this.physics.activate(owner);
      if (!c) return;
      const point = this.physics.cellPosition(anchor)!;
      const local = this.physics.toLocal(c, point);
      let broken: number | undefined;
      for (const cell of this.physics.cellRegions.query(c.cells, local, Math.sqrt(42))) {
        const p = physicalCenter(cell);
        if ((p.x - local.x) ** 2 + (p.y - local.y) ** 2 + (p.z - local.z) ** 2 > 42) continue;
        cell.heat = entry.heat;
        if (MATERIALS[cell.material].fuel > 0 && entry.heat > 0.55) {
          cell.hp -= entry.elapsed * entry.heat * 0.075;
          if (cell.hp <= 0) broken = cell.id;
        }
      }
      if (broken !== undefined) {
        const p = this.physics.cellPosition(broken);
        if (p) this.physics.hit(c, p, 0.08, 0.2);
      } else if (c.anchored) {
        const last = this.assessedHeat.get(id);
        if (!last || (this.physics.time - last.time > 0.6 && Math.abs(entry.heat - last.heat) > 0.12)) {
          this.assessedHeat.set(id, { heat: entry.heat, time: this.physics.time });
          this.physics.requestAssessment(c);
        }
      }
    };
    entry.run = run;
    entry.queued = this.physics.jobs.push(run);
  }
  /** Retain accumulated thermal exposure when the structural queue is full. */
  flush(): void {
    let scheduled = 0;
    for (const entry of this.pending.values()) {
      if (entry.queued) continue;
      if (scheduled >= 8 || !this.physics.jobs.push(entry.run)) break;
      entry.queued = true;
      scheduled++;
    }
  }
  get pendingHeat(): number {
    return this.pending.size;
  }
  clear(): void {
    this.physics.supportObservers.delete(this.markChanged);
    this.fuelScans = this.fuelCellsVisited = 0;
    this.anchors.clear();
    this.points.clear();
    this.fixedPoints.clear();
    this.pending.clear();
    this.seen.clear();
    this.marked.clear();
    this.assessedHeat.clear();
  }
}
